import { useController } from 'react-hook-form';
import { ui } from '@procuros/datachecks';

import { useErrorContext } from 'contexts/ErrorsNavigationContext/ErrorsNavigationContext';
import { WebEDIError } from 'services/webediErrors/types';
import { useEnumerableConfigurations } from 'components/DataTable/cells/WebEDI/cellHelpers';
import { DisplayModes } from 'components/DataTable/Types';
import { DataDisplay } from 'components/Form/DataDisplay/DataDisplay';
import { SchemableFormRadioGroup } from 'components/Form/SchemableFormRadioGroup/SchemableFormRadioGroup';
import { useFieldState } from 'components/SchemableForm/hooks/useFieldState';

type RadioFieldProps = {
  field: ui.RadioField;
  fieldId: string;
  isReadOnly: boolean;
  mode: DisplayModes;
  variant?: 'large';
};

export const RadioField = ({ field, fieldId, isReadOnly, mode, variant }: RadioFieldProps) => {
  const { field: formField } = useController({ name: fieldId });
  const { service } = useErrorContext<WebEDIError>();
  const error = service.useErrorById(fieldId);
  const numberOfErrors = service.useNumberOfActiveErrors();
  const setCurrentError = service.setCurrentError;
  const state = useFieldState(fieldId, error);
  const { formattedValue } = useEnumerableConfigurations({
    meta: {
      optionalConfig: 'type_config' in field ? field.type_config : undefined,
      type: field.type,
    },
    value: formField.value,
  });

  if (mode === DisplayModes.view) {
    return (
      <DataDisplay
        value={formattedValue}
        label={field.label}
        totalNumberOfErrors={numberOfErrors}
        error={error}
        fieldId={fieldId}
        onShowFieldErrorModal={() => error && setCurrentError(error)}
        description={field.description}
      />
    );
  }

  return (
    <SchemableFormRadioGroup
      field={field}
      value={formField.value}
      onChange={formField.onChange}
      onBlur={formField.onBlur}
      error={error}
      totalNumberOfErrors={numberOfErrors}
      onShowFieldErrorModal={() => error && setCurrentError(error)}
      required={field.modality === 'MANDATORY'}
      disabled={isReadOnly}
      fieldState={state}
      variant={variant}
      name={formField.name}
    />
  );
};
